import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useParams, useSearchParams } from 'react-router-dom';
import { useCatalog } from '../app/providers/CatalogProvider.jsx';

const MuxPlayer = React.lazy(() => import('@mux/mux-player-react'));

function CatalogState({ children }) {
  const { status, error, retry } = useCatalog();
  if (status === 'loading') {
    return (
      <div className="catalog-state catalog-loading" aria-busy="true">
        <div className="loading-line wide" />
        <div className="loading-line" />
        <div className="loading-card-row" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, index) => <div className="loading-card" key={index} />)}
        </div>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="catalog-state error">
        <h1>We couldn’t load the catalog.</h1>
        <p>{error?.message || 'Please try again.'}</p>
        <button type="button" onClick={retry}>Try Again</button>
      </div>
    );
  }
  return children;
}

function itemPath(item) {
  return item?.kind === 'series' ? `/series/${encodeURIComponent(item.id)}` : `/title/${encodeURIComponent(item?.id || '')}`;
}

function primaryCtaPath(item) {
  if (!item?.id) return '/home';
  return item.kind === 'series' ? itemPath(item) : `/watch/${encodeURIComponent(item.id)}`;
}

function primaryCtaLabel(item) {
  return item?.kind === 'series' ? 'Explore Series' : 'Watch Now';
}

function ArtworkImage({ src, mobileSrc = '', alt, className = '', eager = false }) {
  const [failed, setFailed] = useState(false);
  const [mobileFailed, setMobileFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    setMobileFailed(false);
  }, [src, mobileSrc]);

  if ((!src && !mobileSrc) || failed) {
    return (
      <div className={`artwork-fallback ${className}`} role="img" aria-label={alt}>
        <span>{alt}</span>
      </div>
    );
  }

  const responsive = Boolean(mobileSrc && mobileSrc !== src);
  const image = (
    <img
      className={className}
      src={src || mobileSrc}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={eager ? 'high' : 'auto'}
      onError={() => {
        // A missing custom mobile upload must not leave the whole Hero blank.
        if (responsive && !mobileFailed && src && window.matchMedia('(max-width: 767px)').matches) {
          setMobileFailed(true);
        } else {
          setFailed(true);
        }
      }}
    />
  );
  if (!responsive) return image;
  return (
    <picture className="hero-responsive-picture">
      {!mobileFailed ? <source media="(max-width: 767px)" srcSet={mobileSrc} /> : null}
      {image}
    </picture>
  );
}

function HeroTitle({ item }) {
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => setLogoFailed(false), [item?.titleLogo]);

  if (!item?.titleLogo || logoFailed) return <h1>{item?.title}</h1>;

  return (
    <img
      className="hero-title-logo"
      src={item.titleLogo}
      alt={item.title}
      loading="eager"
      decoding="async"
      fetchPriority="high"
      onError={() => setLogoFailed(true)}
    />
  );
}

function titleMeta(item) {
  const kind = item?.kind === 'series' ? 'Series' : 'Movie';
  return [kind, item?.year, item?.duration, ...(item?.genres || []).slice(0, 2)].filter(Boolean).map(String);
}

function TitleCard({ item, variant = 'poster' }) {
  const landscape = variant === 'landscape';
  const artwork = landscape ? (item.backdrop || item.image) : (item.image || item.backdrop);
  return (
    <NavLink className={`catalog-card${landscape ? ' catalog-card-landscape' : ''}`} to={itemPath(item)} aria-label={`View ${item.title}`}>
      <div className="catalog-card-art">
        <ArtworkImage className="catalog-card-image" src={artwork} alt={`${item.title} artwork`} />
        <div className="catalog-card-shade" aria-hidden="true" />
        {item.original ? <span className="original-badge">VIM Original</span> : null}
        {landscape ? (
          <div className="catalog-card-overlay">
            <strong>{item.title}</strong>
            <span>{titleMeta(item).slice(0, 3).join(' • ')}</span>
          </div>
        ) : null}
      </div>
      {!landscape ? (
        <div className="catalog-card-copy">
          <strong>{item.title}</strong>
          <span>{item.kind === 'series' ? 'Series' : 'Movie'}{item.year ? ` • ${item.year}` : ''}</span>
        </div>
      ) : null}
    </NavLink>
  );
}

function Rail({ title, items, viewAll, variant = 'poster' }) {
  if (!items.length) return null;
  return (
    <section className={`catalog-rail catalog-rail-${variant}`}>
      <div className="rail-heading">
        <h2>{title}</h2>
        {viewAll ? <NavLink to={viewAll}>View all</NavLink> : null}
      </div>
      <div className="catalog-track">
        {items.slice(0, 14).map((item) => <TitleCard key={item.id} item={item} variant={variant} />)}
      </div>
    </section>
  );
}

function homeRailVariant(rail, index) {
  const label = String(rail?.title || '').toLowerCase();
  if (/continue|trending|featured|new|popular|recommended|because you watched/.test(label)) return 'landscape';
  return index < 2 ? 'landscape' : 'poster';
}

function boundedMs(value, fallback, min = 0, max = 30000) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
}

function heroImageHoldMs(manager) {
  return boundedMs(manager?.imageHoldMs, 1200, 0, 15000);
}

function heroFadeMs(manager) {
  return boundedMs(manager?.fadeMs, 600, 0, 5000);
}

function heroTrailerPlaybackId(item) {
  const promotion = item?.promotion?.raw || {};
  const raw = item?.raw || {};
  if (promotion.assetsResolved === true) return String(promotion.trailerPlaybackId || '').trim();
  return String(
    promotion.trailerPlaybackId ||
    promotion.trailer_playback_id ||
    promotion.trailerPlaybackID ||
    promotion.trailer?.playbackId ||
    promotion.trailer?.playback_id ||
    raw.trailerPlaybackId ||
    raw.trailer_playback_id ||
    raw.trailerPlaybackID ||
    raw.trailer?.playbackId ||
    raw.trailer?.playback_id ||
    ''
  ).trim();
}

function heroPreviewEnabled(manager, playbackId) {
  if (!playbackId) return false;
  if (manager?.fadeToTrailer === false) return false;
  if (manager?.autoplayMuted === false) return false;
  return true;
}

function HeroTrailerPreview({ item, manager = null, detail = false }) {
  const playbackId = heroTrailerPlaybackId(item);
  const enabled = heroPreviewEnabled(manager, playbackId);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setMounted(false);
    setVisible(false);
    setFailed(false);
    if (!item || !enabled) return undefined;
    const timer = window.setTimeout(() => setMounted(true), heroImageHoldMs(manager));
    return () => window.clearTimeout(timer);
  }, [item?.id, playbackId, enabled, manager?.imageHoldMs]);

  if (!mounted || failed) return null;

  return (
    <React.Suspense fallback={null}>
      <MuxPlayer
        className={`${visible ? 'hero-trailer-preview is-visible' : 'hero-trailer-preview'}${detail ? ' detail-hero-trailer-preview' : ''}`}
        playbackId={playbackId}
        autoPlay="muted"
        muted
        defaultHiddenCaptions
        loop
        playsInline
        preload="metadata"
        metadata={{
          video_id: `${item.id}-${detail ? 'detail' : 'catalog'}-hero-trailer`,
          video_title: `${item.title} Hero Trailer`,
          player_name: 'WatchVIM V3 Hero Preview',
        }}
        onPlaying={() => setVisible(true)}
        onError={() => {
          setVisible(false);
          setFailed(true);
        }}
      />
    </React.Suspense>
  );
}

function PromotedHeroCarousel({ items, manager, sectionLabel = 'WatchVIM' }) {
  const slides = (items || []).filter(Boolean);
  const [activeIndex, setActiveIndex] = useState(0);
  const active = slides.length ? slides[Math.min(activeIndex, slides.length - 1)] : null;
  const trailerPlaybackId = heroTrailerPlaybackId(active);
  const previewEnabled = heroPreviewEnabled(manager, trailerPlaybackId);

  useEffect(() => {
    setActiveIndex(0);
  }, [slides.map((slide) => slide.id).join('|')]);

  function selectSlide(index) {
    setActiveIndex(index);
  }

  if (!active) return null;

  return (
    <div
      className="home-hero catalog-hero collection-hero"
      data-cms-promotion={active.promotion ? 'true' : 'false'}
      data-hero-trailer={previewEnabled ? 'enabled' : 'image-only'}
      data-hero-rotation="manual"
      style={{ '--hero-trailer-fade-ms': `${heroFadeMs(manager)}ms` }}
    >
      <ArtworkImage key={`${active.id}:${active.backdrop}:${active.mobileBackdrop}`} className="hero-media" src={active.backdrop || active.mobileBackdrop || active.image} alt={`${active.title} hero artwork`} eager />
      {previewEnabled ? <HeroTrailerPreview key={active.id} item={active} manager={manager} /> : null}
      <div className="hero-vignette" aria-hidden="true" />
      <div className="home-hero-content">
        <HeroTitle item={active} />
        <div className="hero-facts" aria-label={`${active.title} details`}>
          {titleMeta(active).slice(0, 4).map((value) => <span key={value}>{value}</span>)}
        </div>
        <p className="hero-summary">{active.description || 'Watch on WatchVIM.'}</p>
        <div className="home-hero-actions">
          <NavLink className="hero-button primary" to={primaryCtaPath(active)}><span aria-hidden="true">▶</span> {primaryCtaLabel(active)}</NavLink>
          <NavLink className="hero-button secondary" to={itemPath(active)}>More Info</NavLink>
        </div>
      </div>
      {slides.length > 1 ? (
        <div className="hero-carousel-controls" aria-label={`${sectionLabel} promoted titles`}>
          <button type="button" className="hero-arrow" aria-label="Previous promoted title" onClick={() => selectSlide((activeIndex - 1 + slides.length) % slides.length)}>‹</button>
          <div className="hero-dots">
            {slides.map((slide, index) => (
              <button
                key={`${slide.id}-${index}`}
                type="button"
                className={index === activeIndex ? 'hero-dot active' : 'hero-dot'}
                aria-label={`Show ${slide.title}`}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={() => selectSlide(index)}
              />
            ))}
          </div>
          <button type="button" className="hero-arrow" aria-label="Next promoted title" onClick={() => selectSlide((activeIndex + 1) % slides.length)}>›</button>
        </div>
      ) : null}
    </div>
  );
}

export function HomeCatalogPage() {
  const { catalog } = useCatalog();
  const cmsHeroes = catalog.programming?.heroes?.Home || [];
  const cmsAuthoritative = catalog.programming?.sourceOfTruth === 'watchvim-cms';
  const fallbackHero = catalog.featured[0] || catalog.rails?.[0]?.items?.[0] || catalog.originals[0] || catalog.titles[0];
  const heroes = cmsAuthoritative ? cmsHeroes : (cmsHeroes.length ? cmsHeroes : (fallbackHero ? [fallbackHero] : []));
  const curatedRails = (catalog.rails || []).slice(0, 10);
  const fallbackRails = [
    { id: 'featured', title: 'Featured Titles', items: catalog.featured },
    { id: 'originals', title: 'WatchVIM Originals', items: catalog.originals, viewAll: '/originals' },
    { id: 'series', title: 'Series', items: catalog.shows, viewAll: '/shows' },
    { id: 'movies', title: 'Movies', items: catalog.movies, viewAll: '/movies' },
    { id: 'shorts', title: 'Shorts', items: catalog.shorts, viewAll: '/shorts' },
  ];
  const homeRails = curatedRails.length ? curatedRails : fallbackRails;

  return (
    <CatalogState>
      <section className="home-foundation">
        <PromotedHeroCarousel
          items={heroes}
          manager={catalog.programming?.heroManagers?.Home}
          sectionLabel="WatchVIM"
        />
        <div className="catalog-rails">
          {homeRails.map((rail, index) => (
            <Rail
              key={rail.id || rail.title}
              title={rail.title}
              items={rail.items || []}
              viewAll={rail.viewAll}
              variant={homeRailVariant(rail, index)}
            />
          ))}
        </div>
      </section>
    </CatalogState>
  );
}

const SECTION_HERO_KEYS = {
  movies: 'Movies',
  shows: 'Shows',
  originals: 'Originals',
};

export function CatalogGridPage({ kind, title }) {
  const { catalog } = useCatalog();
  const items = catalog[kind] || [];
  const heroKey = SECTION_HERO_KEYS[kind];
  const cmsHeroes = heroKey ? (catalog.programming?.heroes?.[heroKey] || []) : [];
  return (
    <CatalogState>
      <section className="catalog-page">
        {cmsHeroes.length ? (
          <PromotedHeroCarousel items={cmsHeroes} manager={catalog.programming?.heroManagers?.[heroKey]} sectionLabel={title} />
        ) : (
          <header className="collection-heading">
            <h1>{title}</h1>
          </header>
        )}
        {items.length ? (
          <div className="catalog-grid">
            {items.map((item) => <TitleCard key={item.id} item={item} />)}
          </div>
        ) : <p className="catalog-muted">No titles are currently listed in this collection.</p>}
      </section>
    </CatalogState>
  );
}

export function SearchCatalogPage() {
  const { catalog } = useCatalog();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [draft, setDraft] = useState(query);
  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return catalog.titles.filter((item) => [item.title, item.description, ...(item.genres || [])].join(' ').toLowerCase().includes(needle));
  }, [catalog.titles, query]);

  function submit(event) {
    event.preventDefault();
    const next = draft.trim();
    setSearchParams(next ? { q: next } : {});
  }

  return (
    <CatalogState>
      <section className="catalog-page search-page">
        <header className="collection-heading compact">
          <h1>Search</h1>
        </header>
        <form className="catalog-search-form" role="search" onSubmit={submit}>
          <label className="sr-only" htmlFor="catalog-search">Search WatchVIM</label>
          <input id="catalog-search" type="search" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Search movies, series, genres…" autoComplete="off" />
          <button type="submit">Search</button>
        </form>
        {query ? (
          <>
            <p className="search-summary">{results.length} result{results.length === 1 ? '' : 's'} for “{query}”</p>
            {results.length ? <div className="catalog-grid search-results">{results.map((item) => <TitleCard key={item.id} item={item} />)}</div> : <p className="catalog-muted">No WatchVIM titles matched your search.</p>}
          </>
        ) : <p className="catalog-muted">Search the WatchVIM catalog by title, description, or genre.</p>}
      </section>
    </CatalogState>
  );
}

export function GenreCatalogPage() {
  const { genre = '' } = useParams();
  const { catalog } = useCatalog();
  const decoded = decodeURIComponent(genre).replace(/-/g, ' ');
  const items = catalog.titles.filter((item) => (item.genres || []).some((value) => value.toLowerCase() === decoded.toLowerCase()));
  const heading = items[0]?.genres.find((value) => value.toLowerCase() === decoded.toLowerCase()) || decoded.replace(/\b\w/g, (character) => character.toUpperCase());
  return (
    <CatalogState>
      <section className="catalog-page">
        <header className="collection-heading compact">
          <span className="eyebrow">Genre</span>
          <h1>{heading}</h1>
        </header>
        {items.length ? <div className="catalog-grid">{items.map((item) => <TitleCard key={item.id} item={item} />)}</div> : <p className="catalog-muted">No titles are currently available in this genre.</p>}
      </section>
    </CatalogState>
  );
}

function findTitle(catalog, id) {
  return catalog.titles.find((item) => item.id === id || item.slug === id);
}

export function TitleDetailPage({ actions = null }) {
  const { titleId } = useParams();
  const { catalog } = useCatalog();
  const item = findTitle(catalog, titleId);
  return <CatalogState>{item ? <Detail item={item} actions={actions} /> : <NotFound label="Title" />}</CatalogState>;
}

export function SeriesDetailPage() {
  const { seriesId, season } = useParams();
  const [params] = useSearchParams();
  const { catalog } = useCatalog();
  const item = findTitle(catalog, seriesId);
  const selectedSeason = season || params.get('season');
  return <CatalogState>{item ? <Detail item={item} showEpisodes selectedSeason={selectedSeason} /> : <NotFound label="Series" />}</CatalogState>;
}

function normalizedSeason(value) {
  const season = String(value ?? 1).trim();
  return season || '1';
}

function compareSeasons(left, right) {
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) return leftNumber - rightNumber;
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
}

function EpisodeSeasons({ item, selectedSeason }) {
  const seasons = useMemo(() => {
    const grouped = new Map();
    for (const episode of item.episodes || []) {
      const season = normalizedSeason(episode.season);
      if (!grouped.has(season)) grouped.set(season, []);
      grouped.get(season).push(episode);
    }
    return [...grouped.entries()]
      .sort(([left], [right]) => compareSeasons(left, right))
      .map(([season, episodes]) => ({ season, episodes }));
  }, [item.episodes]);

  if (!seasons.length) return <p className="catalog-muted">Episodes are being prepared for this title.</p>;

  const requestedSeason = normalizedSeason(selectedSeason || seasons[0].season);
  const active = seasons.find((entry) => entry.season === requestedSeason) || seasons[0];
  const activeTabId = `season-tab-${encodeURIComponent(item.id)}-${encodeURIComponent(active.season)}`;
  const activePanelId = `season-panel-${encodeURIComponent(item.id)}-${encodeURIComponent(active.season)}`;

  return (
    <>
      <div className="episode-list-heading">
        <h2>Episodes</h2>
        <nav className="season-tabs" role="tablist" aria-label={`${item.title} seasons`}>
          {seasons.map((entry) => {
            const isActive = entry.season === active.season;
            const tabId = `season-tab-${encodeURIComponent(item.id)}-${encodeURIComponent(entry.season)}`;
            const panelId = `season-panel-${encodeURIComponent(item.id)}-${encodeURIComponent(entry.season)}`;
            return (
              <NavLink
                key={entry.season}
                id={tabId}
                className={isActive ? 'season-tab is-active' : 'season-tab'}
                to={`/series/${encodeURIComponent(item.id)}/season/${encodeURIComponent(entry.season)}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={panelId}
                data-season={entry.season}
              >
                Season {entry.season}
              </NavLink>
            );
          })}
        </nav>
      </div>
      <div
        id={activePanelId}
        className="episode-season-panel"
        role="tabpanel"
        aria-labelledby={activeTabId}
        data-active-season={active.season}
      >
        {active.episodes.map((episode) => (
          <NavLink
            className="episode-row"
            key={`${active.season}-${episode.episode}-${episode.id}`}
            to={`/episode/${encodeURIComponent(item.id)}/${episode.season}/${episode.episode}`}
            data-season={active.season}
          >
            <div className="episode-art">
              <ArtworkImage className="episode-image" src={episode.image || item.backdrop || item.image} alt={`${episode.title} episode thumbnail`} />
            </div>
            <div className="episode-copy">
              <span>Season {episode.season} • Episode {episode.episode}</span>
              <h3>{episode.title}</h3>
              <p>{episode.description}</p>
            </div>
          </NavLink>
        ))}
      </div>
    </>
  );
}

function Detail({ item, showEpisodes = false, selectedSeason = '', actions = null }) {
  const trailerPlaybackId = heroTrailerPlaybackId(item);
  const previewEnabled = heroPreviewEnabled(null, trailerPlaybackId);

  return (
    <section className="detail-page">
      <div
        className="detail-hero"
        data-hero-trailer={previewEnabled ? 'enabled' : 'image-only'}
        style={{ '--hero-trailer-fade-ms': `${heroFadeMs(null)}ms` }}
      >
        <ArtworkImage className="hero-media" src={item.backdrop || item.image} alt={`${item.title} hero artwork`} eager />
        {previewEnabled ? <HeroTrailerPreview key={item.id} item={item} detail /> : null}
        <div className="hero-vignette" aria-hidden="true" />
        <div className="detail-copy">
          <HeroTitle item={item} />
          <div className="hero-facts">{titleMeta(item).map((value) => <span key={value}>{value}</span>)}</div>
          <p className="hero-summary">{item.description || 'Watch on WatchVIM.'}</p>
          {actions || (
            <div className="home-hero-actions detail-hero-actions">
              <NavLink className="hero-button primary" to={primaryCtaPath(item)}><span aria-hidden="true">▶</span> {primaryCtaLabel(item)}</NavLink>
              {trailerPlaybackId ? <NavLink className="hero-button secondary" to={`/trailer/${encodeURIComponent(item.id)}`}>Watch Trailer</NavLink> : null}
            </div>
          )}
        </div>
      </div>
      {showEpisodes ? (
        <div className="episode-list">
          <EpisodeSeasons item={item} selectedSeason={selectedSeason} />
        </div>
      ) : null}
    </section>
  );
}

export function EpisodeDetailPage() {
  const { seriesId, season, episode } = useParams();
  const { catalog } = useCatalog();
  const series = findTitle(catalog, seriesId);
  const item = series?.episodes.find((entry) => String(entry.season) === String(season) && String(entry.episode) === String(episode));
  return (
    <CatalogState>
      {series && item ? (
        <section className="episode-detail-page">
          <NavLink className="back-link" to={`/series/${encodeURIComponent(series.id)}`}>← Back to {series.title}</NavLink>
          <div className="episode-detail-art">
            <ArtworkImage className="episode-detail-image" src={item.image || series.backdrop || series.image} alt={`${item.title} episode artwork`} eager />
          </div>
          <span className="eyebrow">Season {item.season} • Episode {item.episode}</span>
          <h1>{item.title}</h1>
          <p>{item.description || `An episode of ${series.title}.`}</p>
          <div className="playback-placeholder">Playback arrives in Phase 3.</div>
        </section>
      ) : <NotFound label="Episode" />}
    </CatalogState>
  );
}

function NotFound({ label }) {
  return <section className="catalog-state"><h1>{label} not found.</h1><NavLink to="/home">Return Home</NavLink></section>;
}
